import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { DataCenterDiagram } from "@/components/landing/DataCenterDiagram";
import { DC_DEFINITION, DC_PARTS } from "@/lib/dataCenterParts";

const Detail = ({ part }) => (
  <div data-testid="dc-detail" aria-live="polite" className="mt-8 border-l-2 border-[#10B981] pl-5">
    <p className="font-mono-label text-[10px] !tracking-normal text-[#078552]">{part.title}</p>
    <p data-testid="dc-detail-fact" className="mt-2 text-base text-[#0F1117] leading-relaxed">{part.fact}</p>
    <p className="mt-4 font-mono-label text-[10px] !tracking-normal text-[#5F6168]">Roles that run it</p>
    <ul className="mt-2 flex flex-wrap gap-2">
      {part.roles.map((role) => <li key={role} className="border border-[#D5D8DD] bg-white px-2.5 py-1 text-xs text-[#0F1117]">{role}</li>)}
    </ul>
  </div>
);

const PIN_STYLES = {
  chrome: { on: "dc-pin-chrome dc-pin-chrome-on text-[#0F1117]", off: "dc-pin-chrome text-[#0F1117]" },
  green: { on: "bg-[#10B981] border-2 border-[#10B981] text-white", off: "bg-white border-2 border-[#0F1117] text-[#0F1117] hover:bg-[#EFFBF5]" },
};
const IS_PREVIEW = typeof window !== "undefined" && !/(^|\.)dcyb\.ai$/.test(window.location.hostname);

const PinStyleSwitch = ({ value, onChange }) => (
  <div data-testid="dc-pin-style-switch" role="group" aria-label="Number style preview" className="inline-flex border hairline bg-white">
    {[["chrome", "Chrome silver"], ["green", "Original green"]].map(([id, label]) => (
      <button key={id} type="button" data-testid={`dc-pin-style-${id}`} aria-pressed={value === id} onClick={() => onChange(id)}
        className={`px-3 py-1.5 font-mono-label text-[10px] !tracking-normal transition-colors ${value === id ? "bg-[#0F1117] text-white" : "text-[#5F6168] hover:bg-[#F7F8F9]"}`}>{label}</button>
    ))}
  </div>
);

const Pins = ({ active, onSelect, look }) => DC_PARTS.map((part, index) => (
  <button key={part.id} type="button" data-testid={`dc-pin-${part.id}`} aria-label={`Show ${part.title}`} aria-pressed={active === part.id} onClick={() => onSelect(part.id)}
    style={{ left: `${part.pin.x}%`, top: `${part.pin.y}%` }}
    className={`dc-pin absolute -translate-x-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full font-mono-label font-bold text-[11px] sm:text-xs lg:text-sm !tracking-normal flex items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0F1117] ${PIN_STYLES[look][active === part.id ? "on" : "off"]}`}>
    {index + 1}
  </button>
));

const Tiles = ({ active, onSelect }) => (
  <ul className="mt-6 grid grid-cols-2 lg:grid-cols-4 border-l border-t hairline bg-white">
    {DC_PARTS.map(({ id, Icon, title, line }) => (
      <li key={id} className="border-r border-b hairline">
        <button type="button" data-testid={`dc-tile-${id}`} aria-pressed={active === id} onClick={() => onSelect(id)}
          className={`group relative w-full h-full text-left p-4 sm:p-6 flex flex-col gap-3 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#0F1117] ${active === id ? "bg-[#F2FBF7]" : "hover:bg-[#F7F8F9]"}`}>
          <span aria-hidden="true" className={`absolute left-0 right-0 top-0 h-[3px] bg-[#10B981] origin-left transition-transform duration-300 ${active === id ? "scale-x-100" : "scale-x-0"}`} />
          <span className={`w-10 h-10 flex items-center justify-center border ${active === id ? "border-[#10B981] text-[#078552] bg-white" : "border-[#D5D8DD] text-[#0F1117]"}`}><Icon size={18} aria-hidden="true" /></span>
          <span className="font-display text-base sm:text-lg text-[#0F1117]">{title}</span>
          <span className="text-xs sm:text-sm text-[#3D4049] leading-relaxed">{line}</span>
        </button>
      </li>
    ))}
  </ul>
);

export const DataCenterExplainer = () => {
  const [active, setActive] = useState("compute");
  const [look, setLook] = useState("chrome");
  const current = DC_PARTS.find((part) => part.id === active);
  return (
    <section id="what-is-a-data-center" data-testid="dc-explainer-section" aria-labelledby="dc-explainer-title" className="relative bg-white border-b hairline">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-5 items-end">
          <div className="lg:col-span-6 min-w-0">
            <p className="font-mono-label text-[11px] !tracking-normal text-[#5F6168]">/ The basics</p>
            <h2 id="dc-explainer-title" className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.12] sm:leading-[1.12] text-[#0F1117] text-balance">What Is a Data Center?</h2>
          </div>
          <p data-testid="dc-definition" className="lg:col-span-6 text-base sm:text-lg text-[#3D4049] leading-relaxed">{DC_DEFINITION}</p>
        </div>
      </div>
      <figure data-testid="dc-diagram" className="relative mt-10 md:mt-14 w-full border-y hairline bg-white">
        <figcaption className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex flex-wrap items-center justify-between gap-3 font-mono-label text-[10px] !tracking-normal text-[#5F6168]"><span>Fig. 01 · Inside a modern data center</span><span className="flex items-center gap-3">{IS_PREVIEW && <PinStyleSwitch value={look} onChange={setLook} />}<span>Tap a number</span></span></figcaption>
        <div className={`relative mx-auto ${look === "chrome" ? "dc-look-chrome" : ""}`} style={{ maxWidth: "min(1600px, calc((100svh - 130px) * 1.49))" }}><DataCenterDiagram active={active} /><Pins active={active} onSelect={setActive} look={look} /></div>
      </figure>
      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 items-end">
          <div className="lg:col-span-8 min-w-0"><Detail part={current} /></div>
          <div className="lg:col-span-4 lg:text-right"><Link to="/careers" data-testid="dc-careers-link" className="mt-8 group chrome-btn-dark inline-flex items-center gap-2 font-mono-label text-[11px] !tracking-normal px-6 py-3.5">See the roles that run them<ArrowUpRight size={14} aria-hidden="true" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></Link></div>
        </div>
        <Tiles active={active} onSelect={setActive} />
      </div>
    </section>
  );
};
